<template>
  <section class="block-card">
    <div
      :class="[
        'block-card__container',
        { 'block-card__container--reverse': block.options.alternativeLayout },
        { 'block-card__container--single': block.options.fullWidth }
      ]"
    >
      <div class="block-card__content">
        <div class="block-card__content__container">
          <div class="block-card__content__icon" v-if="block.icon">
            <img :src="block.icon" />
          </div>
          <h3>{{ block.heading }}</h3>
          <div>
            <div v-html="block.body" class="block-card__content__copy" />
            <ul v-if="block.pictograms.length" class="pictograms">
              <li
                v-for="pictogram in block.pictograms"
                :key="pictogram.id"
                class="pictogram"
              >
                <img
                  ref="img"
                  v-if="pictogram.icon"
                  :src="pictogram.icon.src"
                  :srcset="pictogram.icon.srcset"
                  :alt="pictogram.icon.title"
                />
                <span class="pictogram__copy">{{ pictogram.text }}</span>
              </li>
            </ul>
            <div v-if="cta" class="block-card__content__copy__cta">
              <CtaItem
                :link="`${cta.link}`"
                :colour="cta.colour"
                :label="cta.label"
                :bubble="cta.bubble"
                :disabled="cta.disabled"
                :disableIcon="cta.disableIcon"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="block-card__media">
        <div class="block-card__media__container">
          <video
            v-if="video"
            controls
            controlsList="nodownload"
            :poster="image ? image.src : null"
            class="card__video"
          >
            <source :src="video.url" :type="video.type" />
          </video>
          <img
            v-else-if="image"
            ref="img"
            :src="image.src"
            :srcset="image.srcset"
            :alt="image.title"
          />
        </div>
      </div>
    </div>

    <div v-if="!block.options.fullWidth" class="block-card__divider">
      <div class="block-card__divider__line"></div>
    </div>
  </section>
</template>

<script>
import { bind, unbind } from '@/utils/eventListener';
import CtaItem from '@/components/Cta/CtaItem';

export default {
  name: 'BlockCard',
  components: {
    CtaItem
  },
  props: {
    block: [Array, Object]
  },
  computed: {
    image() {
      return this.block && this.block.media && this.block.media.images
        ? this.block.media.images[0]
        : null;
    },
    video() {
      return this.block && this.block.media && this.block.media.video.source
        ? this.block.media.video.source[0]
        : null;
    },
    cta() {
      return this.block.ctaGroup ? this.block.ctaGroup[0] : null;
    }
  },

  mounted() {
    const { img } = this.$refs;

    if (img.complete) {
      this.$emit('loaded');
    } else {
      bind(img, 'load', this.handleLoad);
    }
  },

  beforeDestroy() {
    const { img } = this.$refs;
    unbind(img, 'load', this.handleLoad);
  },

  methods: {
    handleLoad() {
      this.$emit('loaded');
    }
  }
};
</script>

<style lang="postcss">
.block-card {
  overflow: hidden;
  @apply max-w-max mx-auto;
  &__container {
    display: grid;
    grid-template-columns: 1fr;
    gap: 4rem;
    padding-top: 3rem;
    @screen lg {
      grid-template-columns: repeat(2, 1fr);
      gap: 0;
      padding-bottom: 0;
    }
    &--reverse {
      @screen sm {
        @apply flex-row-reverse;
      }
    }
  }
  &__media {
    @apply z-40;
    @apply flex;
  }
  &__content {
    @apply z-40;
    @apply flex;
    @apply text-center;
    @apply w-full;
    @screen lg {
      padding-right: 3rem;
      @apply text-left;
    }
    &__icon {
      @apply py-8;
      img {
        @apply mx-auto;
        width: 80px;
        @screen sm {
          @apply mx-0;
          width: 100px;
        }
      }
    }
    h3 {
      @apply font-ginto-regular;
      @apply text-left;
      font-size: calc(28px + (40 - 28) * (100vw - 640px) / (1920 - 640));
      @apply pb-8;
      @screen lg {
        @apply pt-8;
      }
    }
    &__copy {
      @apply font-ginto-regular;
      font-size: calc(12px + (16 - 12) * (100vw - 640px) / (1920 - 640));
      max-width: 75ch;
      @apply text-left;
      p {
        &:not(:last-child) {
          @apply mb-8;
        }
      }
      ul {
        @apply list-disc;
        @apply mt-8 ml-8;
        @apply text-left;
      }
      @screen sm {
        @apply max-w-3xl;
      }
      &__cta {
        margin-top: 2rem;
        margin-bottom: 2rem;
        @apply flex justify-center;
        @screen sm {
          @apply mt-16;
          margin-bottom: 0;
          @apply justify-start;
        }
      }
    }
    &__container {
      width: 100%;
    }
  }
  &__divider {
    @apply flex;
    @apply w-full;
    height: 60px;
    @screen sm {
      height: 125px;
      margin-top: 3rem;
    }
    &__line {
      height: 60px;
      @apply bg-brand-darkerBlue;
      @apply h-full;
      width: 1px;
      @apply mx-auto;
      @screen sm {
        height: 125px;
      }
    }
  }

  ul.pictograms {
    margin: 40px 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(6rem, 1fr));
    gap: 20px;

    img {
      aspect-ratio: 1/1;
      height: clamp(10px, 6rem, 100px);
    }
  }

  .pictogram {
    list-style: none;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    text-align: center;
    justify-content: flex-start;
  }

  .pictogram__copy {
    @apply font-ginto-regular;
    font-size: calc(10px + (16 - 12) * (100vw - 640px) / (1920 - 640));
  }

  &__content__copy__cta {
    @apply justify-start;
  }
}

.blocks {
  .block-card-wrapper {
    &:last-child {
      padding-bottom: 3rem;
    }
    &__inner {
      padding-top: 2rem;
    }
  }
}

@media screen and (min-width: 640px) {
  .blocks {
    .block-card-wrapper {
      &__inner {
        @apply pt-40;
      }
      .block-card__container--reverse {
        @screen lg {
          .block-card__content {
            margin-left: auto;
            padding-right: 0;
            padding-left: 3rem;
          }
          .block-card__media {
            order: -1;
          }
        }
      }
    }
  }
}

.block-card-wrapper:nth-child(n + 3) {
  .block-card-wrapper__inner {
    padding-top: 0;
  }
}

.block-card__container--single {
  padding-top: 5rem;
  padding-bottom: 5rem;
}

.card__video {
  aspect-ratio: 16 / 9;
  object-fit: cover;
}
</style>
